import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import { getUserBps } from 'features/bpList/ducks/selectors'
import { fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import { listPortfoliosRoutine } from 'ducks/portfolio/actions'
import { selectPortfolioList } from 'ducks/portfolio/selectors'
import { selectHasPortfoliosAccess } from 'features/auth/ducks/selectors'

/**
 * Compact BP + Portfolio scope filter, designed to slot into Actions and
 * Incidents toolbars right next to the search input. Both pickers update
 * URL search params (`bp=...` / `portfolio=...`) so the filter is shareable
 * and the page's existing filter logic picks up the change automatically.
 *
 * Hidden when the page is rendered inside a single-BP context (where the
 * `lockedBpId` prop is set) — the page is already scoped to that BP.
 */
const HubScopeFilter = ({
  searchParams,
  setSearchParams,
  lockedBpId,
  lockedPortfolioId,
  lockedEstateId,
  onlyTaskManagement = true,
  bpOnly = false,
  scopedBps = null,
}) => {
  const dispatch = useDispatch()
  const hasPortfoliosAccess = useSelector(selectHasPortfoliosAccess)
  // Defensive: reducers occasionally seed these with non-array shapes during
  // an in-flight fetch; guard so an unexpected `{data: ...}` payload can't
  // crash the host page (Actions/Incidents tabs inside PortfolioDetail).
  const userBpsRaw = useSelector(getUserBps)
  const portfoliosRaw = useSelector(selectPortfolioList)
  const userBps = Array.isArray(userBpsRaw) ? userBpsRaw : []
  const portfolios = Array.isArray(portfoliosRaw) ? portfoliosRaw : []
  const hasScoped = Array.isArray(scopedBps) && scopedBps.length > 0

  // Only fetch what isn't already in the store. Each filter mount used to
  // re-trigger both endpoints unconditionally, which on tab-switch heavy
  // pages produced a flurry of redundant `/portfolios` and
  // `/building-passport` calls. Reading from the existing slice on mount
  // and only refilling when empty cuts that drastically. Skip the portfolios
  // fetch entirely when the user doesn't have portfolios access — otherwise
  // the middleware returns 403 and our interceptor surfaces it as a toast.
  useEffect(() => {
    if (!hasScoped && (!Array.isArray(userBpsRaw) || userBpsRaw.length === 0)) {
      dispatch(fetchUserBpsRoutine({ page: 1, sort: '-created_at' }))
    }
    if (!bpOnly && hasPortfoliosAccess && (!Array.isArray(portfoliosRaw) || portfoliosRaw.length === 0)) {
      dispatch(listPortfoliosRoutine())
    }
    // We deliberately exclude userBpsRaw/portfoliosRaw from the deps —
    // including them would refetch right after the first response lands.
    // The empty check is a one-shot "warm cache" pattern.
  }, [dispatch, hasPortfoliosAccess, bpOnly, hasScoped]) // eslint-disable-line

  const bpOptions = useMemo(() => {
    const source = hasScoped ? scopedBps : (userBps || [])
    return source
      .filter((bp) => hasScoped || !onlyTaskManagement || bp.hasTaskManagement)
      .map((bp) => ({
        id: bp.id,
        label: bp.name || bp.address || `Building Passport ${(bp.id || '').slice(0, 8)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [userBps, scopedBps, hasScoped, onlyTaskManagement])

  const portfolioOptions = useMemo(() => {
    return (portfolios || [])
      .map((p) => ({ id: p.id, label: p.name || `Portfolio ${(p.id || '').slice(0, 8)}` }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [portfolios])

  const selectedBp = useMemo(() => {
    const bpId = searchParams.get('bp')
    return bpOptions.find((o) => o.id === bpId) || null
  }, [bpOptions, searchParams])

  const selectedPortfolio = useMemo(() => {
    const pId = searchParams.get('portfolio')
    return portfolioOptions.find((o) => o.id === pId) || null
  }, [portfolioOptions, searchParams])

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    // Setting BP and Portfolio together makes no sense — clear the other.
    if (key === 'bp' && value) next.delete('portfolio')
    if (key === 'portfolio' && value) next.delete('bp')
    setSearchParams(next, { replace: true })
  }

  // Locked to a single BP (BP-detail context): scope is fixed, hide the
  // filter entirely. In portfolio/estate contexts we still show the BP
  // picker (scoped to that container's buildings) but hide the portfolio
  // picker, since the portfolio/estate itself is already the outer scope.
  if (lockedBpId) return null

  const showPortfolioPicker = !bpOnly && !lockedPortfolioId && !lockedEstateId && hasPortfoliosAccess

  return (
    <Wrap>
      <PickerSlot>
        <Autocomplete
          size="small"
          options={bpOptions}
          value={selectedBp}
          onChange={(_, v) => setParam('bp', v?.id || null)}
          getOptionLabel={(o) => o?.label || ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disableClearable={false}
          noOptionsText="No Building Passports"
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="All Building Passports"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Adornment><ApartmentOutlinedIcon style={{ fontSize: 16 }} /></Adornment>
                ),
              }}
            />
          )}
          sx={{ width: 220 }}
        />
      </PickerSlot>
      {showPortfolioPicker && (
        <PickerSlot>
          <Autocomplete
            size="small"
            options={portfolioOptions}
            value={selectedPortfolio}
            onChange={(_, v) => setParam('portfolio', v?.id || null)}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableClearable={false}
            noOptionsText="No portfolios"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All portfolios"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Adornment><FolderOpenOutlinedIcon style={{ fontSize: 16 }} /></Adornment>
                  ),
                }}
              />
            )}
            sx={{ width: 200 }}
          />
        </PickerSlot>
      )}
    </Wrap>
  )
}

export default HubScopeFilter

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`

const PickerSlot = styled.div`
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    > * { width: 100% !important; }
  }
`

const Adornment = styled.span`
  display: inline-flex;
  align-items: center;
  color: #94a3b8;
  margin-right: 4px;
`
